import { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  Crown,
  Star,
  Users,
  Files,
  Download,
  ThumbsUp,
  Image as ImageIcon,
} from 'lucide-react';
import { BiCommentDetail } from 'react-icons/bi';
import { Button } from '@/components/ui/button';
import ProfilePicture from '@/components/ui/profile-picture';
import { useGetUserByUsernameQuery } from '@/redux/auth/auth.api.slice';
import { useGetPostByIdQuery } from '@/redux/post/post.api.slice';
import { useGetProjectGroupByIdQuery } from '@/redux/projectGroup/projectGroup.api.slice';
import IconTooltip from '@/components/ui/icon-tooltip';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import { IFeedItem } from '@/redux/feed/feed.d';
import type {
  ISpotlightCreator,
  ISpotlightFile,
} from '@/redux/spotlight/spotlight.api.slice';
import { getDisplayName, cn } from '@/lib/utils';

const PANEL =
  'relative isolate overflow-hidden rounded-2xl border border-white/10 shadow-[0_18px_50px_-12px_rgba(6,40,28,0.55)]';

const GLASS =
  'relative isolate overflow-hidden rounded-2xl border border-white/80 bg-card shadow-[0_14px_40px_-8px_rgba(6,78,59,0.28),inset_0_1px_0_rgba(255,255,255,0.9)] supports-[backdrop-filter]:bg-card/70 supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150';

const ETCH = 'pointer-events-none absolute inset-0 -z-10';

const EYEBROW =
  'flex items-center gap-2.5 text-[13px] font-bold uppercase tracking-[0.18em]';

const RULE = 'border-t border-white/10 pt-4';

const LEAD_MIN_H = 'min-h-[260px] sm:min-h-[340px]';

/** Posts and groups live at different detail routes. */
const fileHref = (file: Pick<ISpotlightFile, '_id' | 'kind'>) =>
  file.kind === 'projectGroup'
    ? `/project-groups/${file._id}`
    : `/model/${file._id}`;

/** Light raking across the panel, and a dot field in one corner. */
function Artifacts() {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          ETCH,
          'bg-[repeating-linear-gradient(115deg,transparent_0_38px,rgba(255,255,255,0.05)_38px_40px)]'
        )}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-6 -left-6 -z-10 h-40 w-40 bg-[radial-gradient(circle,rgba(255,255,255,0.18)_1px,transparent_1px)] [background-size:10px_10px] opacity-40"
      />
    </>
  );
}

/** Icon and figure over a caption, ruled off from its neighbour. */
function Stat({
  icon: Icon,
  value,
  label,
  iconClass = 'text-green-400',
}: {
  icon: ComponentType<{ className?: string }>;
  value: string | number | undefined;
  label: string;
  iconClass?: string;
}) {
  return (
    <div className="flex-none border-l border-white/10 pl-3 text-center first:border-l-0 first:pl-0 sm:pl-5">
      <span className="flex items-center justify-center gap-2">
        <Icon className={cn('h-[18px] w-[18px]', iconClass)} />
        <span className="text-xl font-bold leading-none text-white">
          {value ?? 0}
        </span>
      </span>
      <span className="mt-1 block text-[11px] text-white/45">{label}</span>
    </div>
  );
}

function Count({
  icon: Icon,
  value,
  label,
}: {
  icon: ComponentType<{ className?: string }>;
  value: number | undefined;
  label: string;
}) {
  return (
    <span className="flex items-center gap-1.5 text-green-700/60">
      <Icon className="h-[17px] w-[17px]" />
      <span className="text-sm font-semibold text-heading">{value ?? 0}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/* Renders carry their own lettering, so they are shown whole, not cropped. */
function Render({
  src,
  alt,
  className,
}: {
  src: string | null | undefined;
  alt: string;
  className: string;
}) {
  return (
    <span
      className={cn(
        'flex-none overflow-hidden rounded-xl bg-white ring-1 ring-black/5',
        className
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-neutral-300">
          <ImageIcon className="h-10 w-10" />
        </span>
      )}
    </span>
  );
}

export function CreatorCard({ user }: { user: ISpotlightCreator }) {
  // The featured payload carries no creator totals, so the card reads the profile.
  const { data: profile } = useGetUserByUsernameQuery(user.username);
  const { fileCount, likeCount, downloadCount, followersCount } = profile ?? {};

  return (
    <div
      className={cn(
        PANEL,
        LEAD_MIN_H,
        'flex flex-col justify-center gap-4 bg-[radial-gradient(120%_130%_at_16%_50%,#0d4a31_0%,#04170f_52%,#020806_100%)] p-5 transition-shadow hover:shadow-[0_22px_60px_-12px_rgba(34,197,94,0.5)] sm:p-6 sm:pr-9'
      )}
    >
      <Artifacts />

      <Link
        to={`/${user.username}`}
        aria-label={`@${user.username}`}
        className="absolute inset-0"
      />

      <p className={cn(EYEBROW, 'text-green-400')}>
        <Crown className="h-6 w-6 flex-none text-amber-400" />
        Creator of the Month
      </p>

      <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:gap-6 sm:text-left">
        <span className="flex-none rounded-full p-1 ring-2 ring-green-400 [box-shadow:0_0_46px_rgba(34,197,94,0.6)]">
          <ProfilePicture
            src={user.profilePicture ?? undefined}
            alt={getDisplayName(user)}
            className="h-24 w-24 sm:h-[140px] sm:w-[140px]"
          />
        </span>

        <div className="min-w-0 flex-1">
          <p className="break-words font-display text-2xl font-bold leading-tight text-white sm:truncate sm:text-[38px] sm:leading-none">
            @{user.username}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white/55 sm:mt-3 sm:text-base">
            Thanks for sharing your designs and inspiring the CNC community.
          </p>

          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-3 sm:mt-5 sm:flex-nowrap sm:justify-start">
            <Stat icon={Files} value={fileCount} label="Files" />
            <Stat icon={ThumbsUp} value={likeCount} label="Likes" />
            <Stat icon={Download} value={downloadCount} label="Downloads" />
            <Stat icon={Users} value={followersCount} label="Followers" />
          </div>

          <Button
            asChild
            variant="outline"
            className="relative z-10 mt-5 w-full border-green-500 bg-transparent px-8 font-semibold text-green-400 hover:bg-green-500/10 hover:text-green-300 sm:w-auto"
          >
            <Link to={`/${user.username}`}>View profile</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The featured file, mirrored so the render sits opposite the creator's face. */
/** The admin-picked hero shot. Never parsed from G-code: a project whose
 * toolpath the parser chokes on is exactly what the override exists for. */
function HeroShot({ file }: { file: ISpotlightFile }) {
  return (
    <Link
      to={fileHref(file)}
      className="flex w-full flex-none overflow-hidden rounded-xl"
    >
      {file.heroImage ? (
        <img
          src={file.heroImage}
          alt={file.title}
          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-neutral-300">
          <ImageIcon className="h-10 w-10" />
        </span>
      )}
    </Link>
  );
}

function Byline({ user }: { user: ISpotlightCreator }) {
  return (
    <span className="relative z-10 flex items-center gap-2">
      <Link
        to={`/${user.username}`}
        className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-green-300"
      >
        <ProfilePicture
          src={user.profilePicture ?? undefined}
          alt={getDisplayName(user)}
          username={user.username}
          className="h-7 w-7"
        />
        by @{user.username}
      </Link>
      {user.isOnefinityOfficial && (
        <IconTooltip legendKey="onefinity-official">
          <img
            src={OnefinityOfficialIcon}
            className="h-5 w-5"
            alt="onefinity official"
          />
        </IconTooltip>
      )}
    </span>
  );
}

/** The spotlight payload carries no blurb or totals, so the card reads the
 * detail endpoint for whichever kind was picked. The other query is skipped,
 * so a post costs no extra request. */
function useFileDetail(file: ISpotlightFile) {
  const isGroup = file.kind === 'projectGroup';
  const { data: post } = useGetPostByIdQuery(file._id, { skip: isGroup });
  const { data: group } = useGetProjectGroupByIdQuery(
    { id: file._id, light: true },
    { skip: !isGroup }
  );
  const { description, likes, comments, averageTotalRating } =
    (isGroup ? group : post) ?? {};

  return {
    isGroup,
    description,
    likes,
    comments,
    averageTotalRating,
  };
}

/** The admin-picked file, mirrored so the shot sits opposite the creator. */
export function DesignCard({ file }: { file: ISpotlightFile }) {
  const { isGroup, description, likes, comments, averageTotalRating } =
    useFileDetail(file);
  const href = fileHref(file);

  return (
    <div
      className={cn(
        PANEL,
        LEAD_MIN_H,
        'group flex flex-col gap-1 bg-[radial-gradient(120%_140%_at_88%_8%,#1d2733_0%,#0a1016_54%,#03070a_100%)] p-3 transition-shadow hover:shadow-[0_22px_60px_-12px_rgba(4,120,87,0.55)] sm:flex-row-reverse'
      )}
    >
      <Artifacts />

      <Link to={href} aria-label={file.title} className="absolute inset-0" />

      {/* Stacked, the shot has no height to fill and renders at its natural
          aspect — 396px of a 667px viewport for one image. */}
      <div className="flex h-44 w-full sm:h-auto sm:w-[38%] sm:max-w-[310px]">
        <HeroShot file={file} />
      </div>

      {/* Body face, not display: these are the maker's words, and MuseoModerno's
          G reads as a Q at title size. */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-2 sm:p-4">
        <p className={cn(EYEBROW, 'text-white')}>
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-400">
            <Star className="h-[15px] w-[15px] fill-amber-950 text-amber-950" />
          </span>
          File of the Week
        </p>

        <Link to={href} className="min-w-0">
          <p className="line-clamp-2 text-2xl font-bold leading-tight text-white group-hover:text-green-300 sm:text-[32px]">
            {file.title}
          </p>
        </Link>

        {file.user && <Byline user={file.user} />}

        <p className="line-clamp-3 text-sm leading-relaxed text-white/55 sm:text-base">
          {description}
        </p>

        <div
          className={cn(
            RULE,
            'mt-auto flex flex-wrap items-center gap-x-4 gap-y-3 sm:flex-nowrap sm:gap-5'
          )}
        >
          {averageTotalRating != null && (
            <Stat
              icon={Star}
              value={averageTotalRating}
              label="Avg rating"
              iconClass="fill-amber-400 text-amber-400"
            />
          )}
          <Stat
            icon={ThumbsUp}
            value={likes}
            label="Likes"
            iconClass="text-white/50"
          />
          <Stat
            icon={BiCommentDetail}
            value={comments}
            label="Comments"
            iconClass="text-white/50"
          />

          <Button
            asChild
            className="relative z-10 w-full bg-amber-400 font-bold text-black hover:bg-amber-300 sm:ml-auto sm:w-auto"
          >
            <Link to={href}>{isGroup ? 'View project' : 'View file'}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A group counts its projects where a post counts its downloads; the other
 * two read the same, off differently named fields. */
function MiniCounts({ item }: { item: IFeedItem }) {
  if (item.type === 'projectGroup') {
    return (
      <>
        <Count icon={ThumbsUp} value={item.likesCount} label="Likes" />
        <Count
          icon={BiCommentDetail}
          value={item.commentsCount}
          label="Comments"
        />
        <Count icon={Files} value={item.projectCount} label="Projects" />
      </>
    );
  }
  return (
    <>
      <Count icon={ThumbsUp} value={item.likes} label="Likes" />
      <Count icon={BiCommentDetail} value={item.comments} label="Comments" />
      <Count icon={Download} value={item.downloadCount} label="Downloads" />
    </>
  );
}

export function MiniCard({ item }: { item: IFeedItem }) {
  const isGroup = item.type === 'projectGroup';
  const title = isGroup ? item.name : item.title;
  const thumbnail = isGroup ? item.thumbnail : item.postThumbnail;
  const to = isGroup ? `/project-groups/${item._id}` : `/model/${item._id}`;

  return (
    <Link
      to={to}
      className={cn(
        GLASS,
        'group flex flex-col gap-3 p-3 transition-all hover:border-green-500/60 hover:shadow-[0_16px_46px_rgba(4,120,87,0.22),inset_0_1px_0_rgba(255,255,255,0.85)]'
      )}
    >
      <span className="flex items-center gap-3">
        <Render
          src={thumbnail}
          alt={title}
          className="h-[96px] w-[118px] p-2"
        />
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block font-semibold leading-snug text-heading group-hover:text-green-700">
            {title}
          </span>
          <span className="mt-1 block truncate text-xs font-medium text-green-700">
            by {getDisplayName(item.user)}
          </span>
        </span>
      </span>

      <span className="mt-auto flex items-center gap-5 border-t border-green-900/10 pt-3">
        <MiniCounts item={item} />
      </span>
    </Link>
  );
}
